import type { CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical } from 'lucide-react'

import type { Lead } from '@/domains/types'
import { LeadTile } from './LeadTile'

interface SortableLeadTileProps {
  lead: Lead
  investigationId: string
  onDelete: (e: React.MouseEvent) => void
}

/**
 * Drag-and-drop-aware wrapper around `LeadTile`: owns the dnd-kit
 * `useSortable` wiring and builds the drag handle, keeping `LeadTile`
 * itself free of any dnd-kit dependency. Only the handle carries the
 * pointer/keyboard listeners so existing tile buttons (edit/delete/
 * publish/conclusion) keep working without interference from dragging.
 */
export function SortableLeadTile({ lead, ...tileProps }: SortableLeadTileProps) {
  const { t } = useTranslation()
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: lead.id })

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const dragHandle = (
    <button
      type="button"
      ref={setActivatorNodeRef}
      {...attributes}
      {...listeners}
      aria-label={t('investigation_detail.lead_drag_handle')}
      className="cursor-grab touch-none p-1.5 text-muted-foreground hover:text-foreground active:cursor-grabbing transition-colors"
    >
      <GripVertical className="w-4 h-4" />
    </button>
  )

  return (
    <div ref={setNodeRef} style={style}>
      <LeadTile lead={lead} {...tileProps} dragHandle={dragHandle} />
    </div>
  )
}
